import posthog from "posthog-js";
import { config } from "@/config/env";

const enabled = !!config.posthogToken;
const CONSENT_STORAGE_KEY = "campnerd_tracking_consent";
export const TRACKING_CONSENT_RESET_EVENT = "campnerd:tracking-consent-reset";

export type TrackingConsent = "granted" | "denied";

let initialized = false;
let consentRequired = false;
let consent: TrackingConsent | null = readConsent();
let currentAid: string | null = null;
let currentCampground: string | null = null;

function readConsent(): TrackingConsent | null {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    return null;
  }
}

function storeConsent(value: TrackingConsent) {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Consent still applies for the current page when storage is unavailable.
  }
}

function clearStoredConsent() {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Consent is still reset for the current page when storage is unavailable.
  }
}

function canCapture() {
  return !consentRequired || consent === "granted";
}

function registerContext() {
  if (!initialized) return;
  if (currentAid) {
    posthog.register({ aid: currentAid });
  } else {
    posthog.unregister("aid");
  }
  if (currentCampground) posthog.register({ campground: currentCampground });
}

function syncPostHogConsent() {
  if (!initialized) return;

  if (!consentRequired) {
    posthog.clear_opt_in_out_capturing();
    posthog.startSessionRecording();
    return;
  }

  if (consent === "granted") {
    posthog.opt_in_capturing({ captureEventName: false });
    registerContext();
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
}

function initializeAnalytics() {
  if (!enabled || initialized || !canCapture()) return;

  posthog.init(config.posthogToken!, {
    api_host: config.posthogHost ?? "https://eu.i.posthog.com",
    persistence: "memory", // GDPR: no analytics identifiers in browser storage
    autocapture: false, // iframe widget — DOM click capture is noisy/risky
    capture_pageview: false, // handled manually by AnalyticsLifecycle
    capture_pageleave: false, // not meaningful in iframe
    session_recording: {
      maskAllInputs: true, // guard against accidental recording of billing fields
    },
  });
  initialized = true;
  registerContext();
  syncPostHogConsent();
}

export type AnalyticsEvent =
  | "search_started"
  | "alternative_dates_viewed"
  | "alternative_dates_selected"
  | "accommodation_selected"
  | "extras_submitted"
  | "details_submitted"
  | "checkout_submitted"
  | "checkout_failed"
  | "booking_completed";

export const analytics = {
  initialize() {
    initializeAnalytics();
  },

  configureConsentRequirement(required: boolean) {
    consentRequired = required;
    syncPostHogConsent();
  },

  setTrackingConsent(value: TrackingConsent, options: { persist?: boolean } = {}) {
    consent = value;
    if (options.persist !== false) storeConsent(value);
    if (value === "granted") initializeAnalytics();
    syncPostHogConsent();
  },

  resetTrackingConsent() {
    consent = null;
    clearStoredConsent();
    syncPostHogConsent();
    window.dispatchEvent(new Event(TRACKING_CONSENT_RESET_EVENT));
  },

  getTrackingConsent() {
    return consent;
  },

  setAid(aid: string | null) {
    currentAid = aid;
    if (!enabled || !initialized || !canCapture()) return;
    if (aid) {
      posthog.register({ aid });
    } else {
      posthog.unregister("aid");
    }
  },

  setCampground(slug: string) {
    currentCampground = slug;
    if (!enabled || !initialized || !canCapture()) return;
    posthog.register({ campground: slug });
  },

  page(path: string) {
    if (!enabled || !initialized || !canCapture()) return;
    posthog.capture("$pageview", { $current_url: path });
  },

  capture(event: AnalyticsEvent, properties?: Record<string, unknown>) {
    if (!enabled || !initialized || !canCapture()) return;
    posthog.capture(event, properties);
  },
};
